import { z } from "zod";

// ── Option arrays (label for display, value sent to API / HubSpot) ────────────
//
// SOURCE OF TRUTH: packages/schemas/src/schemas/practiceSurvey.ts (gac-m monorepo).
// Keep this file in sync when options are added or renamed there.
//
// IMPORTANT: These values are mirrored as enum options on HubSpot contact
// properties (user_selected_artist_classification, user_selected_primary_medium,
// etc.). Adding or renaming a value here requires a matching update in HubSpot
// before the new value will sync correctly.

export const PROFESSIONAL_LEVEL_OPTIONS = [
  { label: "Professional artist (income from art)", value: "professional" },
  { label: "Serious amateur / hobbyist", value: "hobbyist" },
  { label: "Casual / recreational painter", value: "recreational" },
  { label: "Student", value: "student" },
  { label: "Educator / instructor", value: "educator" },
  { label: "Commercial (illustration, decorative, restoration)", value: "commercial" },
] as const;

export const MEDIUM_OPTIONS = [
  { label: "Acrylics", value: "acrylics" },
  { label: "Oils", value: "oils" },
  { label: "Watercolor", value: "watercolor" },
  { label: "Pastel", value: "pastel" },
  { label: "Mixed Media", value: "mixed-media" },
  { label: "Encaustic", value: "encaustic" },
  { label: "Sculpture", value: "sculpture" },
  { label: "Models", value: "models" },
  { label: "Pen & Pencil", value: "pen-pencil" },
  { label: "Other", value: "other" },
] as const;

export const ANNUAL_SPEND_OPTIONS = [
  { label: "Less than $200", value: "under-200" },
  { label: "$200–$500", value: "200-500" },
  { label: "$500–$1,500", value: "500-1500" },
  { label: "$1,500–$5,000", value: "1500-5000" },
  { label: "$5,000+", value: "over-5000" },
] as const;

export const PURCHASE_LOCATION_OPTIONS = [
  { label: "Local independent art store", value: "local-independent" },
  { label: "Local chain (Blick, Jerry's, etc.)", value: "local-chain" },
  { label: "Online retailer (Amazon, Blick.com, etc.)", value: "online" },
  { label: "Direct from manufacturer", value: "direct" },
  { label: "Mix of the above", value: "mixed" },
] as const;

export const LAST_GOLDEN_PURCHASE_OPTIONS = [
  { label: "Never", value: "never" },
  { label: "More than a year ago", value: "over-a-year" },
  { label: "Within the last year", value: "last-year" },
  { label: "Within the last 6 months", value: "last-6-months" },
  { label: "Within the last month", value: "last-month" },
  { label: "I buy regularly / repeat customer", value: "regular" },
] as const;

export const OTHER_BRAND_OPTIONS = [
  { label: "Liquitex", value: "liquitex" },
  { label: "Winsor & Newton", value: "winsor-newton" },
  { label: "M. Graham", value: "m-graham" },
  { label: "Sennelier", value: "sennelier" },
  { label: "Gamblin", value: "gamblin" },
  { label: "Old Holland", value: "old-holland" },
  { label: "Golden", value: "golden" },
  { label: "Williamsburg", value: "williamsburg" },
  { label: "QoR", value: "qor" },
  { label: "Other", value: "other" },
] as const;

export const BRAND_MEDIUM_MAP: Record<string, readonly string[]> = {
  liquitex: ["acrylics", "mixed-media"],
  "winsor-newton": ["acrylics", "oils", "watercolor", "pastel", "mixed-media", "encaustic"],
  "m-graham": ["acrylics", "oils", "watercolor"],
  sennelier: ["oils", "watercolor", "pastel"],
  gamblin: ["oils", "encaustic"],
  "old-holland": ["oils", "watercolor"],
  golden: ["acrylics", "mixed-media"],
  williamsburg: ["oils", "encaustic"],
  qor: ["watercolor"],
  other: [],
};

// ── Derived enum tuples for Zod ───────────────────────────────────────────────

type ValuesOf<T extends readonly { value: string }[]> = T[number]["value"];

const enumFrom = <T extends readonly { value: string }[]>(opts: T) =>
  opts.map((o) => o.value) as unknown as [ValuesOf<T>, ...ValuesOf<T>[]];

export const PROFESSIONAL_LEVEL_ENUM = enumFrom(PROFESSIONAL_LEVEL_OPTIONS);
export const MEDIUM_ENUM = enumFrom(MEDIUM_OPTIONS);

// ── Schema ────────────────────────────────────────────────────────────────────

export const SubmitPracticeSurveyInputSchema = z.object({
  professionalLevel: z.enum(enumFrom(PROFESSIONAL_LEVEL_OPTIONS)),
  primaryMedium: z.enum(enumFrom(MEDIUM_OPTIONS)),
  additionalMediums: z.array(z.enum(enumFrom(MEDIUM_OPTIONS))),
  annualSpend: z.enum(enumFrom(ANNUAL_SPEND_OPTIONS)),
  lastGoldenPurchase: z.enum(enumFrom(LAST_GOLDEN_PURCHASE_OPTIONS)),
  purchaseLocations: z.enum(enumFrom(PURCHASE_LOCATION_OPTIONS)),
  otherBrands: z.array(z.enum(enumFrom(OTHER_BRAND_OPTIONS))),
});

export type SubmitPracticeSurveyInputType = z.infer<typeof SubmitPracticeSurveyInputSchema>;
